import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Search, MessageSquare, ExternalLink, FileText } from 'lucide-react';

type Doc = {
  documentId: string; documentTitle: string; fileName: string; documentType: string;
  province: string | null; city: string | null; documentUrl: string | null; chunkCount: number; hasFile?: boolean;
};

// Uploaded titles often have collapsed/underscored whitespace — tidy for display.
const clean = (t: string) => (t || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();

export default function Standards() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/pdf/documents')
      .then(r => setDocs((r.data as Doc[]).filter(d => d.documentType === 'Context')))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? docs.filter(d => clean(d.documentTitle).toLowerCase().includes(t)) : docs;
    return [...list].sort((a, b) => clean(a.documentTitle).localeCompare(clean(b.documentTitle)));
  }, [docs, q]);

  const ask = (d: Doc) => navigate('/nam-ai', { state: { seedPrompt: `Summarize the key guidance, principles, and recommendations from "${clean(d.documentTitle)}", and how they apply to a conservation authority's natural asset management work.` } });

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-5">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1 inline-flex items-center gap-2"><BookOpen size={20} /> Guidelines &amp; Standards</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">Reference library of natural-asset-management guidance, principles, and standards — the same sources the assistant draws on.</p>
      </div>

      <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-4 mb-6">
        <div className="flex items-center gap-[10px] bg-[var(--surface)] border border-[var(--border)] rounded-[12px] px-3 py-2 max-w-[460px]">
          <Search size={16} className="text-[color:var(--text-muted)]" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search guidance & standards…" className="flex-1 bg-transparent outline-none text-[14px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]" />
        </div>
      </div>

      {loading && <p className="text-[13px] text-[color:var(--text-muted)]">Loading…</p>}
      {!loading && filtered.length === 0 && <p className="text-[13px] text-[color:var(--text-muted)]">No guidance documents{q ? ` match “${q}”` : ' available yet'}.</p>}

      <div className="grid gap-[12px] [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]">
        {filtered.map(d => (
          <div key={d.documentId} className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[14px] p-4 flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <FileText size={18} className="text-[color:var(--accent)] shrink-0 mt-[2px]" />
              <div className="text-[14px] font-medium text-[color:var(--text-primary)] leading-snug">{clean(d.documentTitle)}</div>
            </div>
            <div className="text-[11px] text-[color:var(--text-muted)]">{d.chunkCount} sections{d.province ? ` · ${d.province}` : ''}</div>
            <div className="flex items-center gap-2 mt-auto pt-1">
              {d.hasFile && <a href={`/api/pdf/file/${d.documentId}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-[8px] border border-[var(--border)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"><BookOpen size={13} /> Read PDF</a>}
              <button onClick={() => ask(d)} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-[8px] bg-[var(--accent)] text-white hover:opacity-90"><MessageSquare size={13} /> Ask about this</button>
              {d.documentUrl && <a href={d.documentUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-[8px] border border-[var(--border)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"><ExternalLink size={13} /> Open link</a>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
